import { useEffect, useState } from 'react';

import { supabase } from '../../../lib/supabase';
import { tokenize } from '../components/Jobs/ranking';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserSkillsContext {
  skills: Set<string>;
  titles: Set<string>;
}

export interface UseUserSkillsResult {
  ctx:       UserSkillsContext | null;
  hasResume: boolean;
  loading:   boolean;
}

// ---------------------------------------------------------------------------
// Shape of parsed_json stored in public.user_resumes
// ---------------------------------------------------------------------------

interface ParsedJson {
  rawText?:    string;
  resumeText?: string;
  structured?: {
    // various casings used across save paths in the codebase
    skills?:                    unknown;
    Skills?:                    unknown;
    'Technical Proficiencies'?: unknown;
    experience?: Array<{ title?: string }>;
  };
}

function collectStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((s): s is string => typeof s === 'string');
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetches the user's latest resume from public.user_resumes and extracts
 * a canonicalized skill set + title token set for job ranking.
 *
 * Returns { ctx: null, hasResume: false } for unsigned-in users or when no
 * resume row exists — callers must treat ranking as optional.
 */
export function useUserSkills(isSignedIn: boolean): UseUserSkillsResult {
  const [ctx,       setCtx]       = useState<UserSkillsContext | null>(null);
  const [hasResume, setHasResume] = useState(false);
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    if (!isSignedIn) {
      setCtx(null);
      setHasResume(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    const run = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || !mounted) return;

        const { data: rows } = await supabase
          .from('user_resumes')
          .select('parsed_json')
          .eq('user_id', session.user.id)
          .order('upload_timestamp', { ascending: false })
          .limit(1);

        if (!mounted) return;

        const row = (rows ?? [])[0] as { parsed_json?: unknown } | undefined;
        if (!row?.parsed_json) {
          setHasResume(false);
          return;
        }

        const parsed = row.parsed_json as ParsedJson;

        // ── Raw text ──────────────────────────────────────────────────────
        const rawText =
          (typeof parsed.rawText    === 'string' ? parsed.rawText    : '') +
          ' ' +
          (typeof parsed.resumeText === 'string' ? parsed.resumeText : '');

        // ── Structured skills (various field names used by save paths) ────
        const structured = parsed.structured ?? {};
        const rawSkills: string[] = [
          ...collectStringArray(structured.skills),
          ...collectStringArray(structured.Skills),
          ...collectStringArray(structured['Technical Proficiencies']),
        ];

        // ── Experience titles ─────────────────────────────────────────────
        const titleTokens: string[] = (structured.experience ?? [])
          .map(e => e.title ?? '')
          .filter(Boolean);

        // ── Build token sets using the same canonicalize fn as ranking.ts ─
        const skillSet = new Set<string>();

        // Explicit skills list takes priority; also tokenise raw text for
        // inline skill mentions ("5 years of React…")
        rawSkills.forEach(s => tokenize(s).forEach(t => skillSet.add(t)));
        tokenize(rawText).forEach(t => skillSet.add(t));

        const titleSet = new Set<string>();
        titleTokens.forEach(t => tokenize(t).forEach(tok => titleSet.add(tok)));

        if (mounted) {
          setHasResume(true);
          setCtx({ skills: skillSet, titles: titleSet });
        }
      } catch {
        // Ranking is a best-effort enhancement; swallow all errors so the
        // jobs page still loads cleanly.
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void run();
    return () => { mounted = false; };
  }, [isSignedIn]);

  return { ctx, hasResume, loading };
}
